import { z } from 'zod';
import { moneySchema } from '../money';
import { salesDocumentKindSchema } from './sales-document.schema';

/** Anzahl Top-Kunden im Chart „Umsatz nach Kunde". */
export const SALES_SUMMARY_TOP_CUSTOMERS = 6;

/** Länge der Monatsreihe (ältester Monat zuerst, laufender Monat zuletzt). */
export const SALES_SUMMARY_REVENUE_MONTHS = 12;

/** Ein Monat der Umsatzreihe: `JJJJ-MM` (Europe/Berlin) + fakturierter Netto-Umsatz. */
export const salesRevenueMonthSchema = z.object({
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'month muss JJJJ-MM sein'),
  net: moneySchema,
});
export type SalesRevenueMonth = z.infer<typeof salesRevenueMonthSchema>;

/**
 * Verkaufs-Kennzahlen über ALLE nicht gelöschten Verkaufsbelege
 * (`GET /v1/sales-documents/summary`, Analyse-Cockpit) — serverseitig gerechnet
 * (2026-09-29). Vorher summierte der Browser die neuesten 200 Belege.
 *
 * Zählregeln aus `revenue-rules.ts` (dieselben wie die Dashboard-Kacheln).
 * Jahr und Monate in Europe/Berlin. Beträge in Mikro-Einheiten (§6), wie im
 * Dashboard währungsübergreifend summiert und als EUR ausgewiesen.
 */
export const salesSummarySchema = z.object({
  asOf: z.string().datetime(),
  /** Laufendes Kalenderjahr (Europe/Berlin) — Bezug für Umsatz und Top-Kunden. */
  year: z.number().int(),
  /** Fakturierter Netto-Umsatz im laufenden Jahr (inkl. vordatierter Belege). */
  revenueNet: moneySchema,
  /** Die letzten `SALES_SUMMARY_REVENUE_MONTHS` Monate inkl. laufendem, ältester zuerst. */
  monthlyRevenue: z.array(salesRevenueMonthSchema),
  /** Auftragsbestand: Netto der Aufträge mit Status versendet/angenommen. */
  orderBacklogNet: moneySchema,
  /** Angebots-Trichter: offen (Entwurf/versendet), angenommen, abgelehnt. */
  quotes: z.object({
    open: z.number().int().nonnegative(),
    accepted: z.number().int().nonnegative(),
    rejected: z.number().int().nonnegative(),
  }),
  /** Anzahl Belege je Belegart (nur belegte Arten, häufigste zuerst). */
  documentsByKind: z.array(z.object({ kind: salesDocumentKindSchema, count: z.number().int().positive() })),
  /** Top-Kunden nach fakturiertem Netto-Umsatz im laufenden Jahr (≠ 0, absteigend). */
  topCustomers: z.array(z.object({ customerName: z.string(), net: moneySchema })),
});
export type SalesSummary = z.infer<typeof salesSummarySchema>;
